import * as React from "react";
import { RefreshCw, Loader2, RotateCcw } from "lucide-react";

import { AppLayout } from "../../../components/ui-v2/layout/AppLayout";
import { Card } from "../../../components/ui-v2/card";
import { Button } from "../../../components/ui-v2/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui-v2/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui-v2/dialog";
import { Heading, Text } from "../../../components/ui-v2/typography";
import { useAdminMetrics } from "../../../hooks/useAdminMetrics";

import { WINDOW_OPTIONS } from "./constants";
import { SystemOverview } from "./SystemOverview";
import { ExternalApisSection } from "./ExternalApisSection";
import { DynamoDbSection } from "./DynamoDbSection";
import { ResponseTimesTable } from "./ResponseTimesTable";

function ResetConfirmDialog({ onConfirm }) {
  const [open, setOpen] = React.useState(false);

  const handleConfirm = () => {
    onConfirm();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <RotateCcw className="h-4 w-4 mr-1" />
          Reset
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset Metrics</DialogTitle>
          <DialogDescription>
            This will reset all metric counters to zero. This action cannot be undone. Are you sure?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm}>
            Reset All Metrics
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminMetrics() {
  const [windowMinutes, setWindowMinutes] = React.useState("60");
  const [autoRefresh, setAutoRefresh] = React.useState(false);

  const { data, loading, error, refresh, resetMetrics } = useAdminMetrics({
    windowMinutes: parseInt(windowMinutes),
    autoRefresh,
    refreshInterval: 30000,
  });

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Compute totals for stat cards
  const totalRequests = React.useMemo(() => {
    if (!data?.responseTime) return 0;
    return Object.values(data.responseTime).reduce((sum, s) => sum + s.count, 0);
  }, [data?.responseTime]);

  const totalApiCalls = React.useMemo(() => {
    if (!data?.externalApis) return 0;
    return Object.values(data.externalApis).reduce((sum, s) => sum + s.totalCalls, 0);
  }, [data?.externalApis]);

  const totalDynamoOps = data?.dynamoDb?.total || 0;
  const uptimeMinutes = data?.summary?.uptimeMinutes || 0;

  return (
    <AppLayout title="System Metrics">
      <div className="space-y-4">
        {/* Header bar */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <Heading level={4} className="mb-0.5">
              System Metrics
            </Heading>
            <Text variant="secondary" size="sm">
              Monitor API usage, database operations, and response times
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <Select value={windowMinutes} onValueChange={setWindowMinutes}>
              <SelectTrigger className="w-[110px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WINDOW_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              {autoRefresh ? "Auto ON" : "Auto OFF"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing || loading}
            >
              {isRefreshing || loading ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Refresh
            </Button>
            <ResetConfirmDialog onConfirm={resetMetrics} />
          </div>
        </div>

        {error && (
          <Card className="p-4 border-destructive">
            <Text variant="destructive" size="sm">
              Error loading metrics: {error}
            </Text>
          </Card>
        )}

        {/* System Overview - Stat Cards */}
        <SystemOverview
          loading={loading}
          uptimeMinutes={uptimeMinutes}
          totalRequests={totalRequests}
          totalApiCalls={totalApiCalls}
          totalDynamoOps={totalDynamoOps}
          windowMinutes={windowMinutes}
        />

        {/* External APIs Section */}
        <ExternalApisSection loading={loading} externalApis={data?.externalApis} />

        {/* DynamoDB Section */}
        <DynamoDbSection loading={loading} dynamoDb={data?.dynamoDb} />

        {/* Response Times Section */}
        <ResponseTimesTable
          loading={loading}
          responseTime={data?.responseTime}
          windowMinutes={windowMinutes}
        />
      </div>
    </AppLayout>
  );
}

export default AdminMetrics;
